import React from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, ReferenceLine, ReferenceArea,
  ResponsiveContainer, Tooltip as RechartsTooltip, LabelList, Cell,
} from 'recharts';

// Colour tokens shared with the approved mock (TR deep green structure, orange only for emphasis).
export const T = {
  floor: '#F8F9FA',
  paper: '#FFFFFF',
  ink: '#1A1A1A',
  muted: '#5D6762',
  line: 'rgba(26,46,34,0.12)',
  line2: 'rgba(26,46,34,0.06)',
  green: '#1A2E22',
  tint: 'rgba(26,46,34,0.05)',
  orange: '#D74B00',
  orangeTint: 'rgba(215,75,0,0.10)',
};

// Effort-allocation segment colours (DESIGN.md: deep green structure, orange only for "needs attention").
export const SEG = {
  goal: '#1A2E22',
  quarterly: '#6E9A80',
  bau: '#C3CCC6',
  unlinked: '#D74B00',
};

const TONES = {
  ok: { bg: 'rgba(46,125,50,0.10)', fg: '#2E7D32' },
  warn: { bg: 'rgba(255,152,0,0.14)', fg: '#A86200' },
  bad: { bg: 'rgba(198,40,40,0.10)', fg: '#C62828' },
  none: { bg: 'rgba(26,46,34,0.06)', fg: '#5D6762' },
};

/** Segmented control (mock `.seg`): white track, active option filled deep green. */
export const SegToggle = ({ value, options, onChange, ariaLabel }) => (
  <Box role="tablist" aria-label={ariaLabel} sx={{ display: 'inline-flex', bgcolor: T.paper, border: `1px solid ${T.line}`, borderRadius: '10px', p: '3px', flexWrap: 'wrap' }}>
    {options.map(([k, label]) => {
      const on = value === k;
      return (
        <Box
          key={k}
          component="button"
          type="button"
          role="tab"
          aria-selected={on}
          onClick={() => onChange(k)}
          sx={{
            border: 0, borderRadius: '8px', px: 1.75, py: 0.875, cursor: 'pointer', font: 'inherit', fontWeight: 500, fontSize: '0.875rem',
            bgcolor: on ? T.green : 'transparent', color: on ? '#fff' : T.muted, transition: 'background-color .2s cubic-bezier(.4,0,.2,1)',
            '&:hover': { bgcolor: on ? T.green : T.tint },
            '&:focus-visible': { outline: `2px solid ${T.green}`, outlineOffset: 2 },
          }}
        >
          {label}
        </Box>
      );
    })}
  </Box>
);

/** Clickable column header with ▲ / ▼ for the active sort and a faint ↕ otherwise. */
export const SortHeader = ({ label, active, dir, onClick, align = 'left' }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    title={`Sort by ${label}`}
    sx={{
      bgcolor: 'transparent', border: 0, p: 0, font: 'inherit', fontWeight: 600, color: 'inherit', cursor: 'pointer',
      display: 'inline-flex', gap: 0.75, alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      '& .arr': { fontSize: 10, color: active ? T.orange : T.muted, opacity: active ? 1 : 0.7 },
      '&:hover .arr': { opacity: 1 },
    }}
  >
    {label}
    <span className="arr" aria-hidden="true">{active ? (dir === 1 ? '▲' : '▼') : '↕'}</span>
  </Box>
);

/** Table header cell style from the mock: console-grey ground, green 600 text, 2px rule. */
export const headCellSx = { bgcolor: T.floor, color: T.green, fontWeight: 600, fontSize: '0.78rem', letterSpacing: '0.02em', borderBottom: `2px solid ${T.line}`, whiteSpace: 'nowrap' };

export const Pill = ({ tone = 'none', children, title }) => {
  const t = TONES[tone] || TONES.none;
  const pill = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.375,
        borderRadius: '8px', bgcolor: t.bg, color: t.fg, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
        '&::before': { content: '""', width: 7, height: 7, borderRadius: '50%', bgcolor: 'currentColor' },
      }}
    >
      {children}
    </Box>
  );
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill;
};

export const Visibility = ({ leadership }) => (
  <Tooltip title={leadership ? 'Shown in the leadership view and the email' : 'Only the team sees this'}>
    <Box
      component="span"
      sx={{
        ml: 'auto', fontSize: '0.72rem', fontWeight: 500, borderRadius: '8px', px: 1, py: 0.25, whiteSpace: 'nowrap',
        ...(leadership
          ? { bgcolor: 'rgba(26,46,34,0.06)', color: 'primary.main' }
          : { border: '1px dashed', borderColor: 'divider', color: 'text.secondary' }),
      }}
    >
      {leadership ? 'Leadership sees this' : 'Team only'}
    </Box>
  </Tooltip>
);

export const AiTag = () => (
  <Box component="span" sx={{ fontSize: '0.72rem', fontWeight: 600, borderRadius: '8px', px: 1, py: 0.25, bgcolor: 'rgba(215,75,0,0.10)', color: 'secondary.main', whiteSpace: 'nowrap' }}>
    AI-drafted · numbers checked
  </Box>
);

export const Section = ({ title, desc, right, children, sx }) => (
  <Paper sx={{ p: { xs: 2, sm: 2.5 }, px: { sm: 3 }, borderRadius: '12px', minWidth: 0, ...sx }}>
    {(title || right) && (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.75, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          {title && <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>{title}</Typography>}
          {desc && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{desc}</Typography>}
        </Box>
        {right}
      </Box>
    )}
    {children}
  </Paper>
);

export const Big = ({ value, unit, size = '2rem' }) => (
  <Typography component="div" sx={{ fontSize: size, fontWeight: 300, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
    {value ?? '—'}
    {unit && value != null && <Box component="small" sx={{ fontSize: '0.9rem', color: 'text.secondary', ml: 0.5 }}>{unit}</Box>}
  </Typography>
);

export const Caption = ({ children, sx }) => (
  <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: '0.78rem', ...sx }}>{children}</Typography>
);

/** Tiny inline trend line for tables. */
export const Spark = ({ values, color = SEG.goal, min, max, width = 90, height = 26 }) => {
  const v = (values || []).map((x) => (x == null ? null : Number(x)));
  const nums = v.filter((x) => x != null);
  if (nums.length < 2) return <Box component="span" sx={{ display: 'inline-block', width }} />;
  let lo = min ?? Math.min(...nums);
  let hi = max ?? Math.max(...nums);
  if (hi === lo) { hi += 1; lo -= 1; }
  const x = (k) => 2 + (k * (width - 6)) / (v.length - 1);
  const y = (n) => height - 3 - ((n - lo) / (hi - lo)) * (height - 6);
  const pts = v.map((n, k) => (n == null ? null : `${x(k)},${y(n)}`)).filter(Boolean).join(' ');
  const lastIdx = v.map((n, k) => (n == null ? -1 : k)).filter((k) => k >= 0).pop();
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline fill="none" stroke={color} strokeWidth="1.6" points={pts} />
      <circle cx={x(lastIdx)} cy={y(v[lastIdx])} r="2.6" fill={color} />
    </svg>
  );
};

export const Dots = ({ items }) => (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    {items.map((it, k) => (
      <Tooltip key={k} title={it.title || ''}>
        <Box
          component="span"
          sx={{
            width: 12, height: 12, borderRadius: '3px', display: 'inline-block',
            ...(it.tone === 'ok' && { bgcolor: '#2E7D32' }),
            ...(it.tone === 'bad' && { bgcolor: '#C62828' }),
            ...(it.tone === 'warn' && { bgcolor: '#E0A100' }),
            ...(it.tone === 'off' && { bgcolor: SEG.bau }),
            ...(it.tone === 'none' && { border: '1.5px dashed', borderColor: 'text.secondary' }),
          }}
        />
      </Tooltip>
    ))}
  </Box>
);

const axis = { fontSize: 10, fill: '#5D6762' };

export const TrendBars = ({ data, dataKey, height = 120, showAverage = true }) => {
  const vals = data.map((d) => d[dataKey]).filter((v) => v != null);
  const avg = vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null;
  return (
    <Box sx={{ height, mt: 1 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 16, right: 8, left: -24, bottom: 0 }}>
          <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={false} />
          <YAxis tick={axis} tickLine={false} axisLine={false} />
          <RechartsTooltip />
          {showAverage && avg != null && <ReferenceLine y={avg} stroke="#5D6762" strokeDasharray="3 3" label={{ value: `avg ${avg}`, position: 'insideTopRight', ...axis }} />}
          <Bar dataKey={dataKey} radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {data.map((d, k) => <Cell key={k} fill={k === data.length - 1 ? SEG.goal : SEG.quarterly} fillOpacity={k === data.length - 1 ? 1 : 0.55} />)}
            <LabelList dataKey={dataKey} position="top" style={axis} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const TrendLine = ({ data, lines, height = 110, band, domain, unit = '' }) => (
  <Box sx={{ height, mt: 1 }}>
    <ResponsiveContainer>
      <LineChart data={data} margin={{ top: 12, right: 12, left: -24, bottom: 0 }}>
        <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={false} />
        <YAxis tick={axis} tickLine={false} axisLine={false} domain={domain || ['auto', 'auto']} />
        <RechartsTooltip formatter={(v) => (v == null ? '—' : `${v}${unit}`)} />
        {band && <ReferenceArea y1={band[0]} y2={band[1]} fill="rgba(46,125,50,0.10)" strokeOpacity={0} />}
        {lines.map((l) => (
          <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2} dot={{ r: 2.5 }} connectNulls isAnimationActive={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </Box>
);
